import {InputHTMLAttributes} from 'react';
import {
	extractDateDigits,
	extractKzPassportDigits,
	extractPersonalNumberDigits,
	extractRecordNumberDigits,
	ForeignDocumentDetailsStepValues,
	ForeignDocumentFieldConfig,
	formatByIdentificationNumberDisplay,
	formatByPassportDisplay,
	formatDateDisplay,
	formatKgPassportDisplay,
	formatPersonalNumberDisplay,
	formatRecordNumberDisplay,
	formatUaPassportDisplay,
	KZ_PASSPORT_DIGITS_PLACEHOLDER,
	normalizeByIdentificationNumber,
	normalizeByPassport,
	normalizeKgPassport,
	normalizeKzPassport,
	normalizePassportNumber,
	normalizeUaPassport,
} from '../../model';

export type ResolvedForeignDocumentFieldControl = {
	name: string;
	value: string;
	placeholder: string;
	normalize: (raw: string) => string;
	clearValue: string;
	inputMode?: InputHTMLAttributes<HTMLInputElement>['inputMode'];
	maxLength?: number;
	multiline?: boolean;
	autoComplete?: string;
	prefix?: string;
	ariaLabel?: string;
};

/** Маппинг типа поля формы → props для общего TextInput-контрола. */
export const resolveForeignDocumentFieldControl = (
	field: ForeignDocumentFieldConfig,
	values: ForeignDocumentDetailsStepValues,
): ResolvedForeignDocumentFieldControl => {
	switch (field.input) {
		case 'passportPlain':
		case 'passportAlphanumeric':
			return {
				name: 'foreignPassportNumber',
				value: values.passportNumber,
				placeholder: field.placeholder,
				normalize: normalizePassportNumber,
				clearValue: '',
				...(field.input === 'passportPlain' ? {inputMode: 'numeric' as const} : {}),
			};
		case 'passportUa':
			return {
				name: 'foreignPassportNumber',
				value: formatUaPassportDisplay(values.passportNumber),
				placeholder: field.placeholder,
				normalize: normalizeUaPassport,
				clearValue: '',
			};
		case 'passportKg':
			return {
				name: 'foreignPassportNumber',
				value: formatKgPassportDisplay(values.passportNumber),
				placeholder: field.placeholder,
				normalize: normalizeKgPassport,
				clearValue: '',
			};
		case 'passportBy':
			return {
				name: 'foreignPassportNumber',
				value: formatByPassportDisplay(values.passportNumber),
				placeholder: field.placeholder,
				normalize: normalizeByPassport,
				clearValue: '',
			};
		case 'passportKz':
			return {
				name: 'foreignPassportNumber',
				value: extractKzPassportDigits(values.passportNumber),
				placeholder: KZ_PASSPORT_DIGITS_PLACEHOLDER,
				normalize: (raw) => normalizeKzPassport(`N${raw}`),
				clearValue: 'N',
				inputMode: 'numeric',
				prefix: 'N',
				ariaLabel: 'Номер паспорта',
			};
		case 'personalNumber':
			return {
				name: 'foreignPersonalNumber',
				value: formatPersonalNumberDisplay(values.personalNumber),
				placeholder: field.placeholder,
				normalize: (raw) => extractPersonalNumberDigits(raw, field.digitsLength),
				clearValue: '',
				inputMode: 'numeric',
				maxLength: field.digitsLength
					? field.digitsLength + Math.floor((field.digitsLength - 1) / 4)
					: undefined,
			};
		case 'identificationAlphanumeric':
			return {
				name: 'foreignPersonalNumber',
				value: formatByIdentificationNumberDisplay(values.personalNumber),
				placeholder: field.placeholder,
				normalize: normalizeByIdentificationNumber,
				clearValue: '',
				maxLength: field.digitsLength,
			};
		case 'recordNumber':
			return {
				name: 'foreignRecordNumber',
				value: formatRecordNumberDisplay(values.recordNumber),
				placeholder: field.placeholder,
				normalize: extractRecordNumberDigits,
				clearValue: '',
				inputMode: 'numeric',
			};
		case 'date':
			return {
				name: `foreign_${field.key}`,
				value: formatDateDisplay(values[field.key]),
				placeholder: field.placeholder,
				normalize: extractDateDigits,
				clearValue: '',
				inputMode: 'numeric',
				autoComplete: field.key === 'birthDate' ? 'bday' : 'off',
			};
		default:
			return {
				name: `foreign_${field.key}`,
				value: values[field.key],
				placeholder: field.placeholder,
				normalize: (raw) => raw,
				clearValue: '',
				multiline: field.input === 'textMultiline',
			};
	}
};
